import { ChevronRight, Star, Store } from "lucide-react";
import { Link } from "react-router-dom";

import type { ApiSchemas } from "@/shared/api/schema";
import { cn } from "@/shared/lib/css";
import { ROUTES } from "@/shared/model/routes";
import { Badge } from "@/shared/ui/kit/badge";
import { Image } from "@/shared/ui/kit/image";
import { MetadataList } from "@/shared/ui/metadata-list";
import { RatingStars } from "@/shared/ui/rating-stars";

type SearchResult = NonNullable<
  ApiSchemas["SearchResultsResponse"]["results"]
>[number];

type SearchResultItemProps = {
  result: SearchResult;
  index: number;
  isSelected: boolean;
  onSelect: (result: SearchResult) => void;
};

export function SearchResultItem({
  result,
  index: _index,
  isSelected,
  onSelect,
}: SearchResultItemProps) {
  return (
    <div>
      <Link
        to={
          result.type === "product"
            ? ROUTES.PRODUCT.replace(":id", String(result.id))
            : ROUTES.STORE.replace(":id", String(result.id))
        }
        onClick={() => onSelect(result)}
        className={cn(
          "group w-full h-auto rounded-lg px-3 py-3 text-sm",
          isSelected && "bg-muted/30",
        )}
      >
        <div className="flex w-full items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted overflow-hidden">
            {result.image_url ? (
              <Image
                src={result.image_url}
                alt={result.title}
                className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-200"
              />
            ) : result.type === "product" ? (
              <Store className="h-5 w-5 text-muted-foreground group-hover:scale-110 transition-transform duration-200" />
            ) : (
              <Star className="h-5 w-5 text-muted-foreground group-hover:scale-110 transition-transform duration-200" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "h-5 px-1.5 rounded text-[9px] font-semibold uppercase tracking-wide",
                  result.type === "product"
                    ? "border-primary/20 text-primary bg-primary/10"
                    : "border-emerald-500/20 text-emerald-500 bg-emerald-500/10",
                )}
              >
                {result.type === "product" ? "С" : "П"}
              </Badge>
              <div className="group-hover:text-primary font-medium leading-5 truncate transition-colors">
                {result.title}
              </div>
              {result.reviews_count > 0 && (
                <RatingStars
                  value={result.average_rating}
                  reviews_count={result.reviews_count}
                  variant="min"
                  size="xs"
                />
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {result.type === "product" ? (
                <MetadataList metadata={result.metadata} variant="compact" />
              ) : result.type === "seller" ? (
                <Badge
                  variant={result.verified ? "secondary" : "outline"}
                  className={cn(
                    "h-5 px-1.5 rounded text-[10px]",
                    result.verified
                      ? "bg-emerald-500/15 text-emerald-500"
                      : "text-muted-foreground",
                  )}
                >
                  {result.verified ? "Проверен" : "Не проверен"}
                </Badge>
              ) : null}
            </div>
          </div>

          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
      </Link>
    </div>
  );
}
